import { Injectable } from '@angular/core';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { Observable, Subject } from 'rxjs';
import { Modal } from '../../models/common';
import { ModalService } from './modal.service';

/**
 * Manages modal workflows.
 *
 * @description Use this service to stitch together a workflow of modals.
 * @export
 * @class ModalWorkflowService
 */
@Injectable({ providedIn: 'root' })
export class ModalWorkflowService {
    public workflowSteps = [] as Modal[];
    public currentStep = 0;
    private _hideWorkflowSubject = new Subject<void>();
    private _hideWorkflowStream = this._hideWorkflowSubject.asObservable();

    constructor(private _modalService: ModalService) {
        SingletonServiceGuard.register('ModalWorkflowService');
    }

    public showWorkflow(steps: Modal[], initialStep = 0, data?: any): void {
        this.workflowSteps = steps.map(
            (modal) => new Modal({ ...modal, data: data || modal.data })
        );
        this.currentStep = initialStep;
        this._modalService.showModal(this.workflowSteps[this.currentStep]);
    }

    public hideWorkflow(): void {
        this.currentStep = 0;
        this._modalService.closeModal();
        this._hideWorkflowSubject.next();
    }

    public nextStep(): void {
        if (this.currentStep < this.workflowSteps.length - 1) {
            this._modalService.showModal(this.workflowSteps[++this.currentStep]);
        }
    }

    public previousStep(): void {
        if (this.currentStep > 0) {
            this._modalService.showModal(this.workflowSteps[--this.currentStep]);
        }
    }

    public updateModals(update: Partial<Modal>): void {
        this.workflowSteps = this.workflowSteps.map((modal) => new Modal({ ...modal, ...update }));
    }

    public get hideWorkflowStream(): Observable<void> {
        return this._hideWorkflowStream;
    }
}
